import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  Save,
  X,
} from 'lucide-react';
import axiosInstance from '../../../utils/axios';
import { isReadOnly } from '../../../utils/permissionUtils';

const MuiTableApp = ({ headers, data, loader, isAdding, setIsAdding, newRow, setNewRow, fetchCall }) => {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoader] = useState(false)

  useEffect(() => {
    setRows(data || []);
  }, [data]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar({ open: false, message: '', severity: 'success' }), 3000);
  };

  const getColumnTitle = (value) => {
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace(/&/g, '&');
  };

  const handleEdit = (bom_id) => setEditingId(bom_id);
  
  const handleSave = async (bom_id) => {
    const editedRow = rows.find(row => row.bom_id === bom_id);
    if (!editedRow) return;

    setLoader(true);
    try {
      const res = await axiosInstance.post(`/bom/v2/update_bom_per_tracker_type`, [editedRow])
      if (res.data?.success) {
        await fetchCall();
      }
      setEditingId(null);
      showSnackbar('Row updated successfully');
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to update row', 'error');
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = () => {
    setRows(data || []);
    setEditingId(null);
  };

  const handleSaveNewRow = async () => {
    if (!newRow.part_number || !newRow.part_description) {
      showSnackbar('Please fill in required fields', 'error');
      return;
    }
    setLoader(true)
    const { part_number, part_description } = newRow;

    const tracker_types = {};
    headers.forEach((header) => {
      if (
        header.value !== 'part_number' &&
        header.value !== 'part_description' &&
        header.value !== 'spare_count' &&
        header.value !== 'additional_count' &&
        header.value !== 'required_quantity' &&
        header.value !== 'received_quantity'
      ) {
        
        const key = Object.entries(newRow?.tracker_types || {}).find(
          ([k, v]) => v.name === header.value
        )?.[0];
        const count = parseFloat(newRow["tracker_types"]?.[key]?.count) || 0;
        tracker_types[header.key] = {
          name: header.value,
          count: count
        };
      }
    });

    const newFormattedRow = {
      part_number,
      description: part_description,
      spare_count: parseFloat(newRow.spare_count) || 0,
      additional_count: parseFloat(newRow.additional_count) || 0,
      required_quantity: newRow.required_quantity ?? 0,
      received_quantity: newRow.received_quantity ?? 0,
      tracker_types,
    };

    try {
      const res = await axiosInstance.post(`/bom/v2/update_bom_per_tracker_type`, [newFormattedRow])
      if(res.data?.success) {
        await fetchCall();
      }
    } catch(err) {
      console.error(err)
    } finally {
      showSnackbar('New row added successfully');
      setLoader(false)
      setIsAdding(false);
      setNewRow({});
    }
  };

  const handleCancelNewRow = () => {
    setIsAdding(false);
    setNewRow({});
  };

  const updateRowData = (bom_id, field, value) => {
    setRows(prevRows => 
      prevRows.map(row => {
        if (row.bom_id === bom_id) {
          if (field === 'part_number' || field === 'part_description') {
            return { ...row, [field === 'part_description' ? 'description' : field]: value };
          } else {
            return {
              ...row,
              tracker_types: {
                ...row.tracker_types,
                [field]: {
                  ...row.tracker_types[field],
                  count: parseFloat(value) || 0
                }
              }
            };
          }
        }
        return row;
      })
    );
  };

  const updateNewRow = (field, value, trackerKey = null, headerValue) => {
    if (trackerKey !== null) {
      setNewRow(prev => ({
        ...prev,
        tracker_types: {
          ...(prev.tracker_types || {}),
          [trackerKey]: {
            name: headerValue || '',
            count: Number(value),
          },
        },
      }));
    } else {
      setNewRow(prev => ({ ...prev, [field]: value }));
    }
  };
  
  const displayRows = isAdding ? [...rows, newRow] : rows;

  const stickyLeftStyle = {
    position: 'sticky',
    left: 0,
    backgroundColor: '#fff',
    zIndex: 3,
    boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  };

  const stickyRightStyle = {
    position: 'sticky',
    right: 0,
    backgroundColor: '#fff',
    zIndex: 3,
    boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
  };

  const stickyHeaderStyle = {
    position: 'sticky',
    top: 0,
    backgroundColor: '#f5f5f5',
    zIndex: 4,
    fontWeight: 600,
  };

  const stickyHeaderLeftStyle = {
    ...stickyLeftStyle,
    ...stickyHeaderStyle,
    zIndex: 5,
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
  };

  const stickyHeaderRightStyle = {
    ...stickyRightStyle,
    ...stickyHeaderStyle,
    zIndex: 5,
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
  };

  const ellipsisStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  };

  const partDescriptionLeftPosition = 150;

  const getColumnStyle = (headerValue, isHeader = false) => {
    const baseEllipsisStyle = isHeader ? ellipsisStyle : {};

    if (headerValue === 'part_number') {
      const baseStyle = isHeader ? stickyHeaderLeftStyle : stickyLeftStyle;
      return { ...baseStyle, ...baseEllipsisStyle, minWidth: 150, width: 150 };
    } else if (headerValue === 'part_description') {
      const baseStyle = isHeader ? stickyHeaderLeftStyle : stickyLeftStyle;
      return {
        ...baseStyle,
        ...ellipsisStyle,
        left: partDescriptionLeftPosition,
        width: 450,
        maxWidth: 450,
        minWidth: 450,
      };
    } else {
      const baseStyle = isHeader ? stickyHeaderStyle : {};
      return { ...baseStyle, ...baseEllipsisStyle, maxWidth: 250, width: 250 };
    }
  };

  const getActionColumnStyle = (isHeader = false) => {
    const baseStyle = isHeader ? stickyHeaderRightStyle : stickyRightStyle;
    const baseEllipsisStyle = isHeader ? ellipsisStyle : {};
    return { ...baseStyle, ...baseEllipsisStyle, minWidth: 100 };
  };

  const CustomTooltip = ({ title, children, ...props }) => (
    <Tooltip
      title={title}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#ffffff',
            color: '#000000',
            border: '1px solid #e0e0e0',
            fontSize: '0.875rem',
            maxWidth: 400,
            '& .MuiTooltip-arrow': {
              color: '#ffffff',
              '&::before': {
                border: '1px solid #e0e0e0',
              },
            },
          },
        },
      }}
      {...props}
    >
      {children}
    </Tooltip>
  );

  const getValue = (value, header) => {
    if (header?.value === 'part_description') {
      return value['description'];
    } else if (header?.value === 'part_number') {
      return value[header?.value];
    } else {
      return value['tracker_types']?.[header?.key]?.['count'] || '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {loader ? (
          <Box sx={{ height: '73vh', display: 'flex', justifyContent: 'center', alignItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ height: '73vh', overflow: 'auto', width: '100%', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader sx={{ minWidth: 1600 }}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => {
                    if (header.hidden) return null;
                    const cellStyle = getColumnStyle(header.value, true);
                    const columnTitle = getColumnTitle(header.value);
                    return (
                      <TableCell key={header.key} sx={cellStyle}>
                        <CustomTooltip title={columnTitle}>
                          <Box sx={ellipsisStyle}>{columnTitle}</Box>
                        </CustomTooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell sx={getActionColumnStyle(true)}>
                    <CustomTooltip title="Actions">
                      <Box sx={ellipsisStyle}>Actions</Box>
                    </CustomTooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows?.map((row) => {
                  const isEditing = editingId === row.bom_id;
                  const isNew = row.isNew;
                  return (
                    <TableRow key={row.bom_id || row.id} hover>
                      {headers.map((header) => {
                        if (header.hidden) return null;
                        const value = getValue(row, header);
                        const cellStyle = getColumnStyle(header.value);
                        return (
                          <TableCell key={header.key} sx={cellStyle}>
                           {isEditing || isNew ? (
                                <TextField
                                  value={value}
                                  onChange={(e) => {
                                    if (header.value === 'part_description' || header.value === 'part_number') {
                                      isNew
                                        ? updateNewRow(header.value, e.target.value)
                                        : updateRowData(row.bom_id, header.value, e.target.value);
                                    } else {
                                      isNew
                                        ? updateNewRow('count', e.target.value, header.key, header.value)
                                        : updateRowData(row.bom_id, header.key, e.target.value);
                                    }
                                  }}
                                  size="small"
                                  fullWidth
                                  variant="outlined"
                                />
                              ) : (
                                <Box sx={ellipsisStyle}>{value}</Box>
                              )}
                          </TableCell>
                        );
                      })}
                      <TableCell sx={getActionColumnStyle(false)}>
                        {isNew || isEditing ? (
                          <>
                            <IconButton loading={loading} onClick={isNew ? handleSaveNewRow : () => handleSave(row.bom_id)} color="primary" size="small">
                              <Save size={18} />
                            </IconButton>
                            <IconButton onClick={isNew ? handleCancelNewRow : handleCancel} color="secondary" size="small">
                              <X size={18} />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton 
                            onClick={() => handleEdit(row.bom_id)} 
                            disabled={isAdding || editingId !== null ||isReadOnly() || true} 
                            color="primary"
                            size="small"
                          >
                            <Edit size={18} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

    </Box>
  );
};

export default MuiTableApp;